/**
 * lex.ivoliq Onboarding-Wizard (Juni 2026) — 5 Schritte:
 * Stammdaten → Steuernummer → Kanzlei als Kunde → Honorarlogik → Vollmachten.
 * Nach Abschluss richtet das Backend automatisch VStP 1,8 %, Istbesteuerung
 * und die monatliche Honorarnote ein.
 */
import { useState, useEffect, useRef } from "react";
import { TITLE_PREFIXES, TITLE_SUFFIXES } from "@/components/lex/lexTitles";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { validateStnr } from "@/utils/lexSteuernummer";
import { Check } from "lucide-react";
import "@/styles/lex.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Stammdaten", "Steuernummer", "Kanzlei", "Honorar", "Vollmachten"];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);

export default function LexOnboardingWizard({ onComplete, allowSkip = false, advisorClient = null, onClose }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    stammdaten: { name: advisorClient?.name || user?.name || "", salutation: "", title_prefix: "", title_suffix: "", street: "", postal_code: "", city: "", vat_id: "", iban: "" },
    steuernummer: "",
    svnr: "",
    birth_date: "",
    kanzlei: { name: "", email: "", street: "", postal_code: "", city: "", vat_id: "", country_code: "AT" },
    honorar: { mode: "fix", amount_net: "", billing_day: 1, billing_period: "previous", start_date: "" },
    vollmachten: { stb_vollmacht: false, finanzonline: false, bank_consent: false, bereits_erteilt: false },
  });

  const set = (section, key, value) =>
    setData((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));

  const uidValid = /^ATU\d{8}$/.test((data.stammdaten.vat_id || "").replace(/\s/g, "").toUpperCase());
  const ibanValid = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test((data.stammdaten.iban || "").replace(/\s/g, "").toUpperCase());
  const stnrError = data.steuernummer.trim() ? validateStnr(data.steuernummer) : null;

  const stepValid = (i) => {
    if (i === 0) return data.stammdaten.name.trim().length > 1 && uidValid && ibanValid;
    if (i === 1) return Boolean((data.steuernummer.trim() && !stnrError) || (data.svnr.trim() && data.birth_date));
    if (i === 2) return data.kanzlei.name.trim().length > 1;
    if (i === 3) return parseFloat(data.honorar.amount_net) > 0;
    if (i === 4) return data.vollmachten.stb_vollmacht || data.vollmachten.bereits_erteilt;
    return false;
  };
  const canNext = () => stepValid(step);
  const allValid = STEPS.every((_, i) => stepValid(i));
  const missingSteps = STEPS.map((label, i) => ({ label, i })).filter(({ i }) => !stepValid(i));

  // ── Zwischenstand (mehrstufiges Ausfüllen): laden beim Öffnen, speichern bei Navigation & Eingaben ──
  const draftLoaded = useRef(false);
  useEffect(() => {
    if (advisorClient) { draftLoaded.current = true; return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/lex/onboarding/draft`);
        const d = res.data?.draft;
        if (d?.data) {
          setData((prev) => ({
            stammdaten: { ...prev.stammdaten, ...(d.data.stammdaten || {}) },
            steuernummer: d.data.steuernummer ?? prev.steuernummer,
            svnr: d.data.svnr ?? prev.svnr,
            birth_date: d.data.birth_date ?? prev.birth_date,
            kanzlei: { ...prev.kanzlei, ...(d.data.kanzlei || {}) },
            honorar: { ...prev.honorar, ...(d.data.honorar || {}) },
            vollmachten: { ...prev.vollmachten, ...(d.data.vollmachten || {}) },
          }));
          if (typeof d.step === "number") setStep(Math.min(Math.max(d.step, 0), STEPS.length - 1));
          toast.info("Ihr gespeicherter Zwischenstand wurde geladen.");
        }
      } catch { /* kein Zwischenstand vorhanden */ }
      draftLoaded.current = true;
    })();
  }, []);

  const saveDraft = (nextStep = step, snapshot = data) => {
    if (advisorClient) return Promise.resolve();
    return axios.put(`${API}/lex/onboarding/draft`, { data: snapshot, step: nextStep }).catch(() => {});
  };

  useEffect(() => {
    if (advisorClient || !draftLoaded.current) return;
    const t = setTimeout(() => saveDraft(step), 800);
    return () => clearTimeout(t);
  }, [data]);

  const goTo = (i) => {
    setStep(i);
    saveDraft(i);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const url = advisorClient ? `${API}/lex/advisor/onboarding/${advisorClient.id}` : `${API}/lex/onboarding`;
      await axios.post(url, {
        stammdaten: {
          ...data.stammdaten,
          vat_id: data.stammdaten.vat_id.replace(/\s/g, "").toUpperCase(),
          iban: data.stammdaten.iban.replace(/\s/g, "").toUpperCase(),
        },
        steuernummer: data.steuernummer || null,
        svnr: data.svnr || null,
        birth_date: data.birth_date || null,
        kanzlei: { ...data.kanzlei, vat_id: data.kanzlei.vat_id || null, email: data.kanzlei.email || null },
        honorar: {
          mode: data.honorar.mode,
          amount_net: parseFloat(data.honorar.amount_net),
          billing_day: parseInt(data.honorar.billing_day, 10) || 1,
          billing_period: data.honorar.billing_period,
          start_date: data.honorar.start_date || null,
        },
        vollmachten: data.vollmachten,
      });
      toast.success(advisorClient
        ? "Einrichtung für den Mandanten abgeschlossen."
        : "Einrichtung abgeschlossen — Ihre Steuerberatung übernimmt ab jetzt.");
      trackEvent("lex_onboarding_completed", { honorar_mode: data.honorar.mode, by_kanzlei: Boolean(advisorClient) });
      onComplete?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Einrichtung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  // Später fertigstellen: Zwischenstand sichern, Anwalt steigt direkt ins Cockpit ein
  const skip = async () => {
    setLoading(true);
    try {
      await saveDraft();
      await axios.post(`${API}/lex/onboarding/skip`, { skipped: true });
      trackEvent("lex_onboarding_skipped");
      toast.info("Ihre Eingaben sind gespeichert — Sie können die Einrichtung jederzeit über den Hinweis oben fertigstellen.");
      onComplete?.();
    } catch {
      toast.error("Überspringen fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lex-theme fixed inset-0 z-[100] overflow-y-auto" style={{ background: "#FDFCFB" }} data-testid="lex-onboarding-wizard">
      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Kopf */}
        <div className="mb-10">
          <div className="flex items-start justify-between">
            <span className="lex-serif text-2xl font-semibold" style={{ color: "#0A1128" }}>lex<span style={{ color: "#C5A059" }}>.</span>ivoliq</span>
            {advisorClient && (
              <button type="button" onClick={onClose}
                className="text-xs uppercase tracking-widest font-semibold text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="lex-ob-cancel-btn">
                Abbrechen ×
              </button>
            )}
          </div>
          <div className="lex-gold-line mt-4 mb-6" />
          <h1 className="lex-serif text-4xl font-semibold" style={{ color: "#0A1128" }}>
            {advisorClient ? `Einrichtung für ${advisorClient.name}.` : "Einmalige Einrichtung."}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {advisorClient
              ? <>Sie führen das Onboarding stellvertretend für <span className="lex-mono">{advisorClient.email}</span> durch.</>
              : "Danach übernimmt Ihre Steuerberatung alles — Sie haben null Aufwand."}
          </p>
          {allowSkip && !advisorClient && (
            <button type="button" onClick={skip} disabled={loading}
              className="mt-4 text-xs uppercase tracking-widest font-semibold underline underline-offset-4 text-slate-400 hover:text-slate-700 transition-colors"
              data-testid="lex-ob-skip-btn">
              Später fertigstellen — direkt zum Cockpit →
            </button>
          )}
        </div>

        {/* Schritt-Anzeige — klickbar: freies Springen zwischen den Schritten */}
        <div className="flex items-center gap-0 mb-10">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <button
                type="button"
                onClick={() => goTo(i)}
                title={`Schritt ${i + 1}: ${s}`}
                className={`flex items-center justify-center w-9 h-9 border text-xs lex-mono font-semibold flex-shrink-0 cursor-pointer transition-colors hover:border-[#C5A059] ${
                  i === step ? "lex-step-active border-slate-300 bg-white" : stepValid(i) ? "lex-step-done" : "border-slate-300 text-slate-400 bg-white"
                }`}
                data-testid={`lex-step-indicator-${i}`}
              >
                {i !== step && stepValid(i) ? <Check className="h-4 w-4" /> : i + 1}
              </button>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-2" />}
            </div>
          ))}
        </div>
        <p className="text-xs uppercase tracking-widest font-semibold mb-6" style={{ color: "#C5A059" }}>
          Schritt {step + 1} / {STEPS.length} — {STEPS[step]}
        </p>

        {/* Inhalt */}
        <div className="lex-card p-8 lex-fade-up" key={step}>
          {step === 0 && (
            <div className="space-y-4">
              {/* Titel (Aug 2026): vorgestellt = Anrede, nachgestellt = nur Honorarnote */}
              <div className="grid grid-cols-3 gap-3">
                <Field label="Anrede">
                  <select className="lex-input" value={data.stammdaten.salutation}
                    onChange={(e) => set("stammdaten", "salutation", e.target.value)} data-testid="lex-ob-salutation-select">
                    <option value="">— keine —</option>
                    <option value="Herr">Herr</option>
                    <option value="Frau">Frau</option>
                  </select>
                </Field>
                <Field label="Titel (vorangestellt)">
                  <select className="lex-input" value={data.stammdaten.title_prefix}
                    onChange={(e) => set("stammdaten", "title_prefix", e.target.value)} data-testid="lex-ob-title-prefix-select">
                    <option value="">— kein Titel —</option>
                    {TITLE_PREFIXES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Titel (nachgestellt)">
                  <select className="lex-input" value={data.stammdaten.title_suffix}
                    onChange={(e) => set("stammdaten", "title_suffix", e.target.value)} data-testid="lex-ob-title-suffix-select">
                    <option value="">— kein Titel —</option>
                    {TITLE_SUFFIXES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <p className="text-xs text-slate-500">
                Der vorangestellte Titel erscheint in der Anrede (z.&nbsp;B. „Herr Mag. Mustermann"),
                der nachgestellte nur auf der Honorarnote.
              </p>
              <Field label="Vollständiger Name (ohne Titel) *">
                <input className="lex-input" value={data.stammdaten.name} onChange={(e) => set("stammdaten", "name", e.target.value)}
                  placeholder="Max Mustermann" data-testid="lex-ob-name-input" />
              </Field>
              <Field label="Straße & Hausnummer">
                <input className="lex-input" value={data.stammdaten.street} onChange={(e) => set("stammdaten", "street", e.target.value)}
                  placeholder="Kärntner Straße 12" data-testid="lex-ob-street-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="PLZ">
                  <input className="lex-input lex-mono" value={data.stammdaten.postal_code} onChange={(e) => set("stammdaten", "postal_code", e.target.value)}
                    placeholder="1010" data-testid="lex-ob-plz-input" />
                </Field>
                <Field label="Ort">
                  <input className="lex-input" value={data.stammdaten.city} onChange={(e) => set("stammdaten", "city", e.target.value)}
                    placeholder="Wien" data-testid="lex-ob-city-input" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Ihre UID *">
                  <input className="lex-input lex-mono" value={data.stammdaten.vat_id}
                    onChange={(e) => set("stammdaten", "vat_id", e.target.value.toUpperCase())}
                    placeholder="ATU12345678" data-testid="lex-ob-uid-input" />
                  {data.stammdaten.vat_id && !uidValid && (
                    <p className="text-xs mt-1" style={{ color: "#B45309" }}>Format: ATU + 8 Ziffern</p>
                  )}
                </Field>
                <Field label="IBAN *">
                  <input className="lex-input lex-mono" value={data.stammdaten.iban}
                    onChange={(e) => set("stammdaten", "iban", e.target.value.toUpperCase())}
                    placeholder="AT12 3456 7890 1234 5678" data-testid="lex-ob-iban-input" />
                  {data.stammdaten.iban && !ibanValid && (
                    <p className="text-xs mt-1" style={{ color: "#B45309" }}>Bitte eine gültige IBAN eingeben</p>
                  )}
                </Field>
              </div>
              <p className="text-xs text-slate-500">
                UID und IBAN erscheinen auf Ihrer Honorarnote — die Kanzlei weiß damit, wohin sie überweist.
              </p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <Field label="Steuernummer (Finanzamt Österreich)">
                <input className="lex-input lex-mono" value={data.steuernummer} onChange={(e) => setData((d) => ({ ...d, steuernummer: e.target.value }))}
                  placeholder="99 999/9999" data-testid="lex-ob-steuernummer-input" />
                {stnrError && (
                  <p className="text-xs mt-1" style={{ color: "#B45309" }} data-testid="lex-ob-steuernummer-error">{stnrError}</p>
                )}
              </Field>
              <p className="text-xs text-slate-500">
                Format: FA-Nummer + Steuernummer (z. B. <span className="lex-mono">03 123/4567</span>).
                Sie steht auf jedem Finanzamts-Bescheid — und ist die Basis für die spätere UVA-Zahlung per QR-Code.
              </p>
              {!data.steuernummer.trim() && (
                <div className="border border-slate-200 bg-white p-4 space-y-4" style={{ borderLeft: "3px solid #C5A059" }} data-testid="lex-ob-svnr-block">
                  <p className="text-xs text-slate-600">
                    Keine Steuernummer zur Hand? Dann genügen <strong>SVNr + Geburtsdatum</strong> —
                    damit trägt Ihre Steuerberatung die FinanzOnline-Vollmacht ohne Rückfrage ein.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="SVNr (10-stellig) *">
                      <input className="lex-input lex-mono" value={data.svnr}
                        onChange={(e) => setData((d) => ({ ...d, svnr: e.target.value }))}
                        placeholder="1234 010180" data-testid="lex-ob-svnr-input" />
                    </Field>
                    <Field label="Geburtsdatum *">
                      <input type="date" className="lex-input lex-mono" value={data.birth_date}
                        onChange={(e) => setData((d) => ({ ...d, birth_date: e.target.value }))}
                        data-testid="lex-ob-birthdate-input" />
                    </Field>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 -mt-1 mb-2">
                Die Kanzlei, für die Sie tätig sind — sie erhält Ihre monatliche Honorarnote.
              </p>
              <Field label="Name der Kanzlei *">
                <input className="lex-input" value={data.kanzlei.name} onChange={(e) => set("kanzlei", "name", e.target.value)}
                  placeholder="Mustermann & Partner Rechtsanwälte GmbH" data-testid="lex-ob-kanzlei-name-input" />
              </Field>
              <Field label="E-Mail (Rechnungsempfang)">
                <input type="email" className="lex-input" value={data.kanzlei.email} onChange={(e) => set("kanzlei", "email", e.target.value)}
                  placeholder="buchhaltung@kanzlei.at" data-testid="lex-ob-kanzlei-email-input" />
              </Field>
              <Field label="Straße & Hausnummer">
                <input className="lex-input" value={data.kanzlei.street} onChange={(e) => set("kanzlei", "street", e.target.value)}
                  placeholder="Opernring 3" data-testid="lex-ob-kanzlei-street-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="PLZ">
                  <input className="lex-input lex-mono" value={data.kanzlei.postal_code} onChange={(e) => set("kanzlei", "postal_code", e.target.value)}
                    placeholder="1010" data-testid="lex-ob-kanzlei-plz-input" />
                </Field>
                <Field label="Ort">
                  <input className="lex-input" value={data.kanzlei.city} onChange={(e) => set("kanzlei", "city", e.target.value)}
                    placeholder="Wien" data-testid="lex-ob-kanzlei-city-input" />
                </Field>
              </div>
              <Field label="UID der Kanzlei (optional)">
                <input className="lex-input lex-mono" value={data.kanzlei.vat_id} onChange={(e) => set("kanzlei", "vat_id", e.target.value.toUpperCase())}
                  placeholder="ATU12345678" data-testid="lex-ob-kanzlei-uid-input" />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                {[["fix", "Fixhonorar", "Gleicher Betrag jeden Monat — vollautomatisch."],
                  ["variabel", "Variabel", "Betrag je Monat — Sie bestätigen mit einem Klick."]].map(([m, label, desc]) => (
                  <button key={m} type="button" onClick={() => set("honorar", "mode", m)}
                    data-testid={`lex-ob-honorar-mode-${m}`}
                    className={`text-left p-4 border transition-colors ${
                      data.honorar.mode === m ? "border-[#C5A059] bg-[#FBF7EF]" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}>
                    <span className="block text-sm font-semibold" style={{ color: "#0A1128" }}>{label}</span>
                    <span className="block text-xs text-slate-500 mt-1">{desc}</span>
                  </button>
                ))}
              </div>
              <Field label={data.honorar.mode === "fix" ? "Monatliches Honorar (netto, EUR) *" : "Voraussichtliches Honorar (netto, EUR) *"}>
                <input type="number" min="1" step="0.01" className="lex-input lex-mono" value={data.honorar.amount_net}
                  onChange={(e) => set("honorar", "amount_net", e.target.value)}
                  placeholder="3500.00" data-testid="lex-ob-honorar-amount-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Rechnungstag im Monat">
                  <input type="number" min="1" max="31" className="lex-input lex-mono" value={data.honorar.billing_day}
                    onChange={(e) => set("honorar", "billing_day", e.target.value)} data-testid="lex-ob-billing-day-input" />
                  <p className="text-xs text-slate-400 mt-1">29.–31. fällt automatisch auf den Monatsletzten</p>
                </Field>
                <Field label="Erste Honorarnote am (optional)">
                  <input type="date" className="lex-input lex-mono" value={data.honorar.start_date}
                    onChange={(e) => set("honorar", "start_date", e.target.value)} data-testid="lex-ob-start-date-input" />
                </Field>
              </div>
              <Field label="Abgerechnet wird">
                <div className="grid grid-cols-2 gap-3">
                  {[["previous", "Vormonat", "Die Note deckt den abgeschlossenen Vormonat ab (Regelfall)."],
                    ["current", "Laufender Monat", "Die Note deckt den Monat ab, in dem sie erstellt wird."]].map(([v, label, desc]) => (
                    <button key={v} type="button" onClick={() => set("honorar", "billing_period", v)}
                      data-testid={`lex-ob-billing-period-${v}`}
                      className={`text-left p-4 border transition-colors ${
                        data.honorar.billing_period === v ? "border-[#C5A059] bg-[#FBF7EF]" : "border-slate-200 bg-white hover:border-slate-300"
                      }`}>
                      <span className="block text-sm font-semibold" style={{ color: "#0A1128" }}>{label}</span>
                      <span className="block text-xs text-slate-500 mt-1">{desc}</span>
                    </button>
                  ))}
                </div>
              </Field>
              <p className="text-xs text-slate-500">
                Umsatzsteuer 20 % wird automatisch ergänzt. Die Vorsteuerpauschale
                <span className="lex-mono" style={{ color: "#C5A059" }}> 1,8 % </span>
                (§ 14 UStG) und die Istbesteuerung werden für Sie aktiviert.
              </p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              {/* Bestandsmandant (Pilot): Vollmacht liegt der Kanzlei bereits vor */}
              <label className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${
                data.vollmachten.bereits_erteilt ? "border-[#C5A059] bg-[#FBF7EF]" : "border-slate-200 bg-white hover:border-slate-300"
              }`} data-testid="lex-ob-vollmacht-bereits_erteilt">
                <input type="checkbox" className="mt-1" checked={data.vollmachten.bereits_erteilt}
                  onChange={(e) => set("vollmachten", "bereits_erteilt", e.target.checked)} />
                <span>
                  <span className="block text-sm font-semibold" style={{ color: "#0A1128" }}>
                    Ich bin bereits Mandant der Kanzlei — die Vollmacht liegt bereits vor
                  </span>
                  <span className="block text-xs text-slate-500 mt-1">
                    Für Bestandsmandanten: Steuerberatungsmandat und FinanzOnline-Vollmacht sind bei Ihrer
                    Kanzlei bereits hinterlegt. Eine erneute Erteilung ist nicht nötig — die Kanzlei bestätigt den Bestand.
                  </span>
                </span>
              </label>

              {data.vollmachten.bereits_erteilt ? (
                <div className="p-4 border" style={{ borderColor: "#C5A059", background: "#FBF7EF" }} data-testid="lex-ob-vollmacht-bereits-info">
                  <p className="text-sm font-semibold" style={{ color: "#0A1128" }}>✓ Vollmachten abgedeckt</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Steuerberater- und FinanzOnline-Vollmacht gelten als erteilt. Ihre Kanzlei prüft den
                    Bestand beim Aktivieren Ihres Mandats im Kanzlei-Portal.
                  </p>
                </div>
              ) : (
              <>
              {[["stb_vollmacht", "Steuerberater-Vollmacht *", "Ihre Steuerberatung darf UVA, Erklärungen und Fristen für Sie übernehmen."],
                ["finanzonline", "FinanzOnline-Vollmacht", "Übermittlung über die Übermittlerkennung Ihrer Steuerberatung (Quotenregelung)."],
                ["bank_consent", "Bank-Consent (Vorbereitung)", "Zustimmung zur späteren Kontoanbindung (PSD2) für automatische Zahlungszuordnung."]].map(([key, label, desc]) => (
                <label key={key} className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${
                  data.vollmachten[key] ? "border-[#C5A059] bg-[#FBF7EF]" : "border-slate-200 bg-white hover:border-slate-300"
                }`} data-testid={`lex-ob-vollmacht-${key}`}>
                  <input type="checkbox" className="mt-1" checked={data.vollmachten[key]}
                    onChange={(e) => set("vollmachten", key, e.target.checked)} />
                  <span>
                    <span className="block text-sm font-semibold" style={{ color: "#0A1128" }}>{label}</span>
                    <span className="block text-xs text-slate-500 mt-1">{desc}</span>
                  </span>
                </label>
              ))}
              {!data.vollmachten.stb_vollmacht && (
                <p className="text-xs" style={{ color: "#B45309" }} data-testid="lex-ob-vollmacht-hint">
                  Die Steuerberater-Vollmacht ist erforderlich, damit lex.ivoliq alles für Sie erledigen kann.
                </p>
              )}
              </>
              )}
            </div>
          )}
        </div>

        {/* Abschluss-Checkliste: offene Pflichtangaben mit Sprung zum Schritt */}
        {step === STEPS.length - 1 && !allValid && (
          <div className="mt-6 p-4 border" style={{ borderColor: "#C5A059", background: "#FBF7EF" }} data-testid="lex-ob-missing-list">
            <p className="text-sm font-semibold" style={{ color: "#0A1128" }}>Für den Abschluss fehlen noch Angaben:</p>
            <ul className="mt-2 space-y-1">
              {missingSteps.map(({ label, i }) => (
                <li key={i}>
                  <button type="button" onClick={() => goTo(i)}
                    className="text-sm underline underline-offset-4 text-slate-600 hover:text-slate-900 transition-colors"
                    data-testid={`lex-ob-missing-step-${i}`}>
                    Schritt {i + 1} — {label} vervollständigen →
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8">
          <button type="button" className="lex-btn-outline" onClick={() => goTo(Math.max(0, step - 1))}
            style={{ visibility: step === 0 ? "hidden" : "visible" }} data-testid="lex-ob-back-btn">
            Zurück
          </button>
          <div className="flex items-center gap-5">
            {step < STEPS.length - 1 && (
              <button type="button" onClick={() => goTo(step + 1)}
                className="text-xs uppercase tracking-widest font-semibold underline underline-offset-4 text-slate-400 hover:text-slate-700 transition-colors"
                data-testid="lex-ob-step-skip-btn">
                Schritt überspringen →
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button type="button" className="lex-btn" disabled={!canNext()} onClick={() => goTo(step + 1)} data-testid="lex-ob-next-btn">
                Weiter
              </button>
            ) : (
              <button type="button" className="lex-btn" disabled={!allValid || loading} onClick={submit} data-testid="lex-ob-submit-btn">
                {loading ? "Wird eingerichtet …" : "Einrichtung abschließen"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
